import React, { useEffect, MouseEvent } from 'react';

type UseClickOutsideParams = {
  targetRef: React.RefObject<HTMLElement>;
  onClickOutside: (e: MouseEvent) => void;
  prioritizeAnchor?: boolean;
  ignoredSelectors?: string[];
  // In some cases using the 'click' event might be problematic, e.g. if users try to select text inside the target component
  // and then release the mouse button once the cursor has been moved outside.
  // Using 'mousedown' in such cases is safer as the event will be fired only when the outside click is triggered deliberately by the user.
  eventType?: 'click' | 'mousedown';
};

const useClickOutside = ({
  targetRef,
  onClickOutside,
  prioritizeAnchor = false,
  ignoredSelectors = [],
  eventType = 'click',
}: UseClickOutsideParams) => {
  useEffect(() => {
    const handleOutsideClick = (e: Event) => {
      if (!targetRef.current) return;

      const target = e.target as Element;
      const anchorClickShouldTakePriority =
        prioritizeAnchor && target.nodeName === 'A';

      if (anchorClickShouldTakePriority) {
        return;
      }
      if (!target.isConnected) {
        return;
      }
      if (ignoredSelectors.some(sel => target.closest(sel))) {
        return;
      }
      if (targetRef.current.contains(target)) {
        return;
      }

      onClickOutside(e as unknown as MouseEvent);
    };

    document.addEventListener(eventType, handleOutsideClick);

    return () => {
      document.removeEventListener(eventType, handleOutsideClick);
    };
  }, [
    onClickOutside,
    prioritizeAnchor,
    targetRef,
    ignoredSelectors,
    eventType,
  ]);
};

export { useClickOutside };
